import geodist from "geodist";
import { Selector } from "react-redux";
import { createSelector, createSelectorCreator } from "reselect";
import { OfferStates } from ".";
import { AppState } from "..";
import { ON_OFFER } from "../../types/offerStates";
import { getClosingTimes } from "./calculateClosingTimes/calculator";
import { selectRestaurantsOfferState } from "./offerSelectors";

export const restaurantsSelector = (state: AppState) => state.restaurants.list;

export const restaurantSelector = (key: string) => (state: AppState) =>
  state.restaurants.list
    ? state.restaurants.list.find(r => r._id === key)
    : null;

export const restaurantsLoadingSelector = (state: AppState) =>
  state.restaurants.list === null;

export const selectRestaurantClosingTimes = (key: string) =>
  createSelector(restaurantSelector(key), restaurant => {
    if (!restaurant) {
      return {};
    }

    return getClosingTimes(restaurant.closingTimes);
  });

export const selectTime = (state: AppState) => state.restaurants.timer;

export const showClosestFirstSelector = (state: AppState) =>
  state.restaurants.showClosestFirst;

export const filterStringSelector = (state: AppState) =>
  state.restaurants.filterString;

export const sortingSelector = (state: AppState) => state.restaurants.sorting;

const locationSelector: Selector<any, any> = state =>
  state.location.currentLocation;

const locationStatusSelector: Selector<any, any> = state =>
  state.location.status;

const sortByDistance = (currentLocation: any) => {
  return (a: RestaurantFromAPI, b: RestaurantFromAPI) => {
    const location = {
      lat: currentLocation.lat,
      lon: currentLocation.lng
    };
    const distA = geodist(
      location,
      { lat: a.lat, lon: a.lng },
      { exact: true }
    );
    const distB = geodist(
      location,
      { lat: b.lat, lon: b.lng },
      { exact: true }
    );
    return distA - distB;
  };
};

function getSortedRestaurants({
  restaurants,
  currentLocation,
  showClosestFirst,
  sorting,
  locationStatus,
  offerStates
}: {
  restaurants: RestaurantFromAPI[];
  currentLocation: UserLocation;
  showClosestFirst: boolean;
  sorting: string;
  locationStatus: string;
  offerStates: OfferStates;
}) {
  return restaurants.sort((a: RestaurantFromAPI, b: RestaurantFromAPI) => {
    const primarySort = sorting
      ? offerStates[b._id] - offerStates[a._id]
      : b[sorting] - a[sorting];
    return showClosestFirst && locationStatus === "LOCATED"
      ? primarySort || sortByDistance(currentLocation)(a, b)
      : primarySort;
  });
}

export const getSortedRestaurantsSelector = createSelector(
  restaurantsSelector,
  locationSelector,
  showClosestFirstSelector,
  filterStringSelector,
  sortingSelector,
  locationStatusSelector,
  selectRestaurantsOfferState,
  (
    restaurants,
    currentLocation,
    showClosestFirst,
    filterString,
    sorting,
    locationStatus,
    offerStates
  ) => {
    return getSortedRestaurants({
      restaurants:
        (restaurants &&
          restaurants.filter(r => {
            if (filterString !== "") {
              const regex = new RegExp(filterString, "g");
              return r.longAddress.match(regex) || r.address.match(regex);
            }
            return true;
          })) ||
        [],
      currentLocation,
      showClosestFirst,
      sorting,
      locationStatus,
      offerStates
    });
  }
);
export const getRestaurantsOnOfferSelector = createSelector(
  restaurantsSelector,
  selectRestaurantsOfferState,
  (restaurants, offerState) => {
    return restaurants
      ? restaurants.filter(r => offerState[r._id] === ON_OFFER)
      : [];
  }
);

function defaultEqualityCheck(currentVal: any, previousVal: any) {
  return currentVal === previousVal;
}

function resultCheckMemoize(
  func: any,
  resultCheck = defaultEqualityCheck,
  argsCheck = defaultEqualityCheck
) {
  let lastArgs: any | null = null;
  let lastResult: any | null = null;
  return (...args: any[]) => {
    if (
      lastArgs !== null &&
      lastArgs.length === args.length &&
      args.every((value, index) => argsCheck(value, lastArgs[index]))
    ) {
      return lastResult;
    }
    lastArgs = args;
    const result = func(...args);
    return resultCheck(lastResult, result) ? lastResult : (lastResult = result);
  };
}

const customCreateSelector = createSelectorCreator(resultCheckMemoize as any);
export const getRestaurantsNotOnOfferSelector = customCreateSelector(
  restaurantsSelector,
  selectRestaurantsOfferState,
  (restaurants, offerState) => {
    return restaurants
      ? restaurants.filter(r => offerState[r._id] !== ON_OFFER)
      : [];
  }
);
